import upperFirst from 'lodash/upperFirst'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import {
  PolicyName,
  Provider,
  OffenseAggregation,
  VehicleEventType_StateEnum,
  VehicleEventType_EventEnum,
  Policy_KindEnum,
  Offense_StatusEnum,
} from 'src/api/gen/api'
import { getEventTypesSection } from 'src/commons/filters/sections'
import { getEnumEntries, getPolicyKindEntries, getProviderEntries } from 'src/commons/filters/utils'
import { sortObjectsByLabel } from 'src/commons/helpers'
import usePolicyKindsExceptFeeRates from 'src/commons/hooks/usePolicyKindsExceptFeeRates'
import { enumsTranslationKeys, withFormattedCount } from 'src/commons/i18nEnums'
import { ITemporalRange } from 'src/commons/temporalHelpers'
import { IEntry } from 'src/components/SidePanel/Components/CheckboxesSection'
import { useAgencySettings } from 'src/layout/AgencySettings'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import TimeRangeFilterPro from 'src/muiv5/components/commons/SidePanel/Filters/TimeRangeFilter/TimeRangeFilterPro'
import { SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'
export interface IViolationListFilters {
  dateRange: ITemporalRange
  eventTypes: VehicleEventType_EventEnum[]
  include_abnormals: boolean
  offenseStatus: Offense_StatusEnum[]
  policyId: string[]
  policyKind: Policy_KindEnum[]
  provider: string[]
  status: VehicleEventType_StateEnum[]
  vin: string
}

interface IProps {
  defaultFilters: IViolationListFilters
  initialFilters: IViolationListFilters
  policies: PolicyName[]
  providers: Provider[]
  violationsCount: number
  violationsFacets: OffenseAggregation
}

export const filterVehiculeEventTypes = (custom_event_types_to_remove: string[], eventTypeList: IEntry[]) => {
  return eventTypeList.filter((event_type) => {
    return !custom_event_types_to_remove.includes(event_type.id)
  })
}

const ViolationsListFilterPanel = forwardRef(function (props: IProps, ref) {
  const { policies, providers, violationsCount, violationsFacets } = props
  const { t } = useTranslation()
  const agencySettings = useAgencySettings()

  const [filters, setFilters] = useState(props.initialFilters)
  useImperativeHandle(ref, () => filters, [filters])

  const enabledPolicyKinds = usePolicyKindsExceptFeeRates()
  const providerEntries = getProviderEntries(providers, violationsFacets.providers)

  const policyKindEntries = getPolicyKindEntries(enabledPolicyKinds, violationsFacets.policy_types)

  const statusEntries = getEnumEntries(
    agencySettings.vehicle_states,
    enumsTranslationKeys.VehicleStates,
    violationsFacets.status
  )

  const eventTypesEntries = getEnumEntries(
    agencySettings.event_types,
    enumsTranslationKeys.EventType,
    violationsFacets.event_types
  )

  const eventTypesSection = getEventTypesSection({
    entries: getEnumEntries(agencySettings.event_types, enumsTranslationKeys.EventType, violationsFacets.event_types),
  })

  eventTypesSection.entries = filterVehiculeEventTypes(
    [VehicleEventType_EventEnum.notification_sent],
    eventTypesSection.entries
  )
  const offenseStatusEntries = getEnumEntries(
    Object.values(Offense_StatusEnum),
    enumsTranslationKeys.OffensesStatus,
    violationsFacets.status
  )

  const policyEntries = policies
    // policies are sorted by published_date in policy_names end_point
    .map(({ id, name }) => ({
      id,
      label: name,
      value: violationsFacets.policy_ids[id],
    }))
    .sort(sortObjectsByLabel)

  function onFilterChange(key: string, newValue: any) {
    setFilters({ ...filters, [key]: newValue })
  }

  function onFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { to: filters.dateRange.to, from: value })
  }

  function onToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { to: value, from: filters.dateRange.from })
  }

  function onBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { from: valueFrom, to: valueTo })
  }

  return (
    <SidePanel
      headerProps={{
        title: t('filtersPanel.title.default'),
        information: t('violationsWithCount', withFormattedCount(violationsCount)),
        onReset: () => setFilters(props.defaultFilters),
      }}
    >
      <CheckboxListFilter
        filterId={'provider'}
        items={providerEntries}
        values={filters['provider']}
        title={upperFirst(t('filtersPanel.provider'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'policyKind'}
        items={policyKindEntries}
        values={filters['policyKind']}
        title={upperFirst(t('pricing.policy_kind'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'status'}
        items={statusEntries}
        values={filters['status']}
        title={upperFirst(t('filtersPanel.status'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'eventTypes'}
        items={eventTypesEntries}
        values={filters['eventTypes']}
        title={upperFirst(t('filtersPanel.event_types'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'policyId'}
        items={policyEntries}
        values={filters['policyId']}
        title={upperFirst(t('pricing.policy'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'offenseStatus'}
        items={offenseStatusEntries}
        values={filters['offenseStatus']}
        title={upperFirst(t('filtersPanel.offense.status'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      <TimeRangeFilterPro
        filterId={'range'}
        format="L"
        values={{
          from: filters.dateRange.from,
          to: filters.dateRange.to,
        }}
        title={upperFirst(t('filters.time_range.offenses'))}
        onFromChanged={onFromChanged}
        onToChanged={onToChanged}
        onBothChanged={onBothChanged}
        disableFuture
        defaultExpanded
      />
    </SidePanel>
  )
})

export default ViolationsListFilterPanel
